/**
 * CRM Data Service
 * Frontend service for all CRM API calls
 * Matches backend routes structure in /backend/routes/crm/
 */

import apiClient from '../../../../apiClient';

class CRMDataService {
  // ===================================
  // DROPDOWN ENDPOINTS (/api/crm/dropdown)
  // ===================================

  async getDropdownData() {
    try {
      const response = await apiClient.get('/crm/dropdown/complete');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      throw error;
    }
  }

  async getStores() {
    try {
      const response = await apiClient.get('/crm/dropdown/stores');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching stores:', error);
      throw error;
    }
  }

  async getCategoriesByTeam(teamId) {
    try {
      const response = await apiClient.get(`/crm/dropdown/teams/${teamId}/categories`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  async getSubcategoriesByCategory(categoryId) {
    try {
      const response = await apiClient.get(`/crm/dropdown/categories/${categoryId}/subcategories`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      throw error;
    }
  }

  // ===================================
  // TEAM ENDPOINTS (/api/crm/teams)
  // ===================================

  async getTeams() {
    try {
      const response = await apiClient.get('/crm/teams');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching teams:', error);
      throw error;
    }
  }

  async getTeamById(teamId) {
    try {
      const response = await apiClient.get(`/crm/teams/${teamId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching team:', error);
      throw error;
    }
  }

  async getTeamMembers(teamCode) {
    try {
      const response = await apiClient.get(`/crm/teams/${teamCode}/members`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching team members:', error);
      throw error;
    }
  }

  // ===================================
  // TICKET ENDPOINTS (/api/crm/tickets)
  // ===================================

  async createTicket(ticketData) {
    try {
      const userEmail = localStorage.getItem('userEmail') || 'default@cafuamanagement.com';
      const response = await apiClient.post('/crm/tickets', {
        ...ticketData,
        requesterEmail: ticketData.requesterEmail || userEmail,
        requesterName: ticketData.requesterName || 'User',
        conditionalFields: ticketData.conditionalFields || {}
      });
      return response.data.data;
    } catch (error) {
      console.error('Error creating ticket:', error);
      throw error;
    }
  }

  async searchTickets(criteria = {}) {
    try {
      const params = new URLSearchParams(criteria);
      const response = await apiClient.get(`/crm/tickets?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error('Error searching tickets:', error);
      throw error;
    }
  }

  async getTicketByNumber(ticketNumber) {
    try {
      const response = await apiClient.get(`/crm/tickets/${ticketNumber}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching ticket:', error);
      throw error;
    }
  }

  async getTicketById(ticketId) {
    try {
      const response = await apiClient.get(`/crm/tickets/id/${ticketId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching ticket comments:', error);
      throw error;
    }
  }

  async assignTicket(ticketNumber, assignData) {
    try {
      const response = await apiClient.post(`/crm/tickets/${ticketNumber}/assign`, assignData);
      return response.data.data;
    } catch (error) {
      console.error('Error assigning ticket:', error);
      throw error;
    }
  }

  async resolveTicket(ticketNumber, resolutionData) {
    try {
      const response = await apiClient.post(`/crm/tickets/${ticketNumber}/resolve`, resolutionData);
      return response.data.data;
    } catch (error) {
      console.error('Error resolving ticket:', error);
      throw error;
    }
  }

  async closeTicket(ticketNumber) {
    try {
      const response = await apiClient.post(`/crm/tickets/${ticketNumber}/close`);
      return response.data.data;
    } catch (error) {
      console.error('Error closing ticket:', error);
      throw error;
    }
  }

  async escalateTicket(ticketNumber, escalationData) {
    try {
      const response = await apiClient.post(`/crm/tickets/${ticketNumber}/escalate`, escalationData);
      return response.data.data;
    } catch (error) {
      console.error('Error escalating ticket:', error);
      throw error;
    }
  }

  async sendTicketReply(ticketNumber, replyData) {
    try {
      const response = await apiClient.post(`/crm/tickets/${ticketNumber}/reply`, replyData);
      return response.data;
    } catch (error) {
      console.error('Error sending ticket reply:', error);
      throw error;
    }
  }

  async reopenTicket(ticketNumber) {
    try {
      const response = await apiClient.post(`/crm/tickets/${ticketNumber}/reopen`);
      return response.data.data;
    } catch (error) {
      console.error('Error reopening ticket:', error);
      throw error;
    }
  }

  async bulkUpdateTickets(ticketNumbers, updates) {
    try {
      const response = await apiClient.post('/crm/tickets/bulk/update', {
        ticketNumbers,
        updates
      });
      return response.data.data;
    } catch (error) {
      console.error('Error bulk updating tickets:', error);
      throw error;
    }
  }

  // ===================================
  // APPROVAL ENDPOINTS (/api/crm/approvals)
  // ===================================

  async getPendingApprovals() {
    try {
      const response = await apiClient.get('/crm/approvals/pending');
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      throw error;
    }
  }

  async actOnApproval(ticketNumber, action, justification) {
    try {
      const response = await apiClient.post(`/crm/approvals/${ticketNumber}`, {
        action,
        justification,
      });
      return response.data.data;
    } catch (error) {
      console.error('Error processing approval action:', error);
      throw error;
    }
  }

  // ===================================
  // FIELD ENDPOINTS (/api/crm/fields)
  // ===================================

  async getFieldsBySubcategory(subcategoryId) {
    try {
      const response = await apiClient.get(`/crm/fields/subcategory/${subcategoryId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching conditional fields:', error);
      throw error;
    }
  }

  async validateFieldValues(subcategoryId, fieldValues) {
    try {
      const response = await apiClient.post('/crm/fields/validate', {
        subcategoryId,
        fieldValues
      });
      return response.data.data;
    } catch (error) {
      console.error('Error validating field values:', error);
      throw error;
    }
  }

  async getTicketFieldValues(ticketId) {
    try {
      const response = await apiClient.get(`/crm/fields/ticket/${ticketId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching ticket field values:', error);
      throw error;
    }
  }

  async createField(fieldData) {
    try {
      const response = await apiClient.post('/crm/fields', fieldData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating field:', error);
      throw error;
    }
  }

  async updateField(fieldId, fieldData) {
    try {
      const response = await apiClient.put(`/crm/fields/${fieldId}`, fieldData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating field:', error);
      throw error;
    }
  }

  async deleteField(fieldId) {
    try {
      const response = await apiClient.delete(`/crm/fields/${fieldId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error deleting field:', error);
      throw error;
    }
  }

  // ===================================
  // DASHBOARD ENDPOINTS (/api/crm/dashboard)
  // ===================================

  async getDashboardSummary(dateRange = {}) {
    try {
      const params = new URLSearchParams(dateRange);
      const response = await apiClient.get(`/crm/dashboard/summary?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      return { total: 0, open: 0, pending: 0, resolved: 0, closed: 0, overdue: 0 };
    }
  }

  async getQuickStats() {
    try {
      const response = await apiClient.get('/crm/dashboard/quick-stats');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching quick stats:', error);
      return {};
    }
  }

  async getTeamStats() {
    try {
      const response = await apiClient.get('/crm/dashboard/team-stats');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching team stats:', error);
      return [];
    }
  }

  async getTicketTrends() {
    try {
      const response = await apiClient.get('/crm/dashboard/trends');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching ticket trends:', error);
      return [];
    }
  }

  async getWorkloadSummary() {
    try {
      const response = await apiClient.get('/crm/dashboard/workload');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching workload summary:', error);
      return [];
    }
  }

  async getSLAOverview() {
    try {
      const response = await apiClient.get('/crm/dashboard/sla');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching SLA overview:', error);
      return {};
    }
  }

  async getAtRiskTickets(pagination = {}) {
    try {
      const params = new URLSearchParams(pagination);
      const response = await apiClient.get(`/crm/dashboard/sla/at-risk?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching at-risk tickets:', error);
      return [];
    }
  }

  async getBreachedTickets(pagination = {}) {
    try {
      const params = new URLSearchParams(pagination);
      const response = await apiClient.get(`/crm/dashboard/sla/breached?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching breached tickets:', error);
      return [];
    }
  }

  async getStoreMetrics(dateRange = {}) {
    try {
      const params = new URLSearchParams(dateRange);
      const response = await apiClient.get(`/crm/dashboard/store-metrics?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching store metrics:', error);
      return [];
    }
  }

  async getPerformanceMetrics(dateRange = {}) {
    try {
      const params = new URLSearchParams(dateRange);
      const response = await apiClient.get(`/crm/dashboard/performance?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
      return {};
    }
  }

  async exportDashboardData(format = 'pdf', dateRange = {}) {
    try {
      const params = new URLSearchParams({ format, ...dateRange });
      const response = await apiClient.get(`/crm/dashboard/export?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error exporting dashboard data:', error);
      throw error;
    }
  }

  // ===================================
  // CONFIG ENDPOINTS (/api/crm/config)
  // ===================================

  async testAssignment(data) {
    try {
      const response = await apiClient.post('/crm/config/test-assignment', data);
      return response.data.data;
    } catch (error) {
      console.error('Error testing assignment:', error);
      throw error;
    }
  }

  // ===================================
  // APPROVAL CONFIG ENDPOINTS (/api/crm/config/approval-*)
  // ===================================

  async getApprovalConfigs() {
    try {
      const response = await apiClient.get('/crm/config/approval-configs');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching approval configs:', error);
      throw error;
    }
  }

  async getApprovalConfigBySubcategory(subcategoryId) {
    try {
      const response = await apiClient.get(`/crm/config/approval-configs/${subcategoryId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching approval config:', error);
      throw error;
    }
  }

  async getApprovalRoles() {
    try {
      const response = await apiClient.get('/crm/config/approval-roles');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching approval roles:', error);
      throw error;
    }
  }

  async saveApprovalConfig(configData) {
    try {
      const response = await apiClient.post('/crm/config/approval-configs', configData);
      return response.data.data;
    } catch (error) {
      console.error('Error saving approval config:', error);
      throw error;
    }
  }

  async deleteApprovalConfig(subcategoryId) {
    try {
      const response = await apiClient.delete(`/crm/config/approval-configs/${subcategoryId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting approval config:', error);
      throw error;
    }
  }

  // ===================================
  // TEAM MANAGEMENT
  // ===================================

  async createTeam(teamData) {
    try {
      const response = await apiClient.post('/crm/teams', teamData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating team:', error);
      throw new Error('Team creation will be available in Phase 2');
    }
  }

  async updateTeam(teamId, teamData) {
    try {
      const response = await apiClient.put(`/crm/teams/${teamId}`, teamData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating team:', error);
      throw new Error('Team update will be available in Phase 2');
    }
  }

  async deleteTeam(teamId) {
    try {
      const response = await apiClient.delete(`/crm/teams/${teamId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error deleting team:', error);
      throw new Error('Team deletion will be available in Phase 2');
    }
  }

  async createCategory(categoryData) {
    try {
      const response = await apiClient.post('/crm/categories', categoryData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  async updateCategory(categoryId, categoryData) {
    try {
      const response = await apiClient.put(`/crm/categories/${categoryId}`, categoryData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  }

  async deleteCategory(categoryId) {
    try {
      const response = await apiClient.delete(`/crm/categories/${categoryId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }

  async createSubcategory(subcategoryData) {
    try {
      const response = await apiClient.post('/crm/subcategories', subcategoryData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating subcategory:', error);
      throw error;
    }
  }

  async updateSubcategory(subcategoryId, subcategoryData) {
    try {
      const response = await apiClient.put(`/crm/subcategories/${subcategoryId}`, subcategoryData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating subcategory:', error);
      throw error;
    }
  }

  async deleteSubcategory(subcategoryId) {
    try {
      const response = await apiClient.delete(`/crm/subcategories/${subcategoryId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      throw error;
    }
  }

  // ===================================
  // TICKET UPDATE ENDPOINT
  // ===================================

  async updateTicket(ticketNumber, updateData) {
    try {
      const response = await apiClient.put(`/crm/tickets/${ticketNumber}`, updateData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating ticket:', error);
      throw error;
    }
  }

  // ===================================
  // COMMENTS ENDPOINTS
  // ===================================

  async getTicketComments(ticketNumber) {
    try {
      // Comments are part of ticket history - filter for comment type entries
      const response = await apiClient.get(`/crm/tickets/${ticketNumber}/history`);
      const history = response.data.data || [];
      // Filter to only show comment-type entries if needed
      return history.filter(entry => entry.type === 'comment' || entry.action_type === 'comment');
    } catch (error) {
      console.error('Error fetching ticket comments:', error);
      throw error;
    }
  }

  async addTicketComment(ticketNumber, commentData) {
    try {
      const response = await apiClient.post(`/crm/tickets/${ticketNumber}/comment`, {
        comment: commentData.comment || commentData.content || commentData,
        type: commentData.type || 'comment'
      });
      return response.data;
    } catch (error) {
      console.error('Error adding ticket comment:', error);
      throw error;
    }
  }

  // ===================================
  // HISTORY ENDPOINTS
  // ===================================

  async getTicketHistory(ticketNumber) {
    try {
      const response = await apiClient.get(`/crm/tickets/${ticketNumber}/history`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching ticket history:', error);
      throw error;
    }
  }

  // ===================================
  // INTERNAL NOTES ENDPOINTS
  // ===================================

  async getInternalNotes(ticketNumber) {
    try {
      const response = await apiClient.get(`/crm/tickets/${ticketNumber}/notes`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching internal notes:', error);
      throw error;
    }
  }

  async addInternalNote(ticketNumber, noteData) {
    try {
      const userEmail = localStorage.getItem('userEmail') || 'system@cafuamanagement.com';
      const userName = localStorage.getItem('userName') || 'System';

      const response = await apiClient.post(`/crm/tickets/${ticketNumber}/notes`, {
        note_content: noteData.note_content || noteData.content || noteData,
        created_by_email: noteData.created_by_email || userEmail,
        created_by_name: noteData.created_by_name || userName
      });
      return response.data.data;
    } catch (error) {
      console.error('Error adding internal note:', error);
      throw error;
    }
  }

  // ===================================
  // LEGACY METHODS (for backward compatibility)
  // ===================================

  async getDashboardStats(period = 'week') {
    return this.getDashboardSummary({ period });
  }

  async getCategoryBreakdown(period = 'last30days') {
    return this.getDashboardSummary({ period });
  }

  async getWeeklyTrends(period = 'last30days') {
    return this.getTicketTrends();
  }

  async getPriorityDistribution(period = 'last30days') {
    return this.getDashboardSummary({ period });
  }
}

export const crmDataService = new CRMDataService();